import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { BookOpen, CircleCheckBig, Gauge, ClipboardList, Users } from 'lucide-react';
import { Skeleton } from './primitives';
import { LabelWithHelp } from './FieldHelp';
import { readMeter, INSTRUCTOR_METERS } from '../services/instructorMeterLimits';
import { DASHBOARD_KPI_HELP } from './kpiFieldHelp';

const ICON = { size: 19, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

/** Per-screen copy and meter wiring for list-page KPI rows. */
export const LIST_STATS_VARIANTS = {
  courses: {
    meterKey: INSTRUCTOR_METERS.courses,
    workspaceIcon: BookOpen,
    workspaceLabel: 'In workspace',
    workspaceMeta: 'Total courses',
    secondaryLabel: 'Enabled',
    secondaryMetaSingle: 'Active right now',
    secondaryMetaPaged: 'Enabled on this page',
    planHelp: DASHBOARD_KPI_HELP.activeCourses,
    planMetaUnlimited: 'No course cap on your plan',
    isSecondaryActive: (row) => row.active === 1,
  },
  assessments: {
    meterKey: INSTRUCTOR_METERS.assessments,
    workspaceIcon: ClipboardList,
    workspaceLabel: 'In workspace',
    workspaceMeta: 'Total assessments',
    secondaryLabel: 'Enabled',
    secondaryMetaSingle: 'Active right now',
    secondaryMetaPaged: 'Enabled on this page',
    planHelp: DASHBOARD_KPI_HELP.assessments,
    planMetaUnlimited: 'No assessment cap on your plan',
    isSecondaryActive: (row) => row.active === 1,
  },
  enrollments: {
    meterKey: INSTRUCTOR_METERS.students,
    workspaceIcon: Users,
    workspaceLabel: 'Enrolled',
    workspaceMeta: 'Total learners',
    secondaryLabel: 'Active',
    secondaryMetaSingle: 'Active right now',
    secondaryMetaPaged: 'Active on this page',
    planHelp: DASHBOARD_KPI_HELP.learners,
    planMetaUnlimited: 'No learner cap on your plan',
    isSecondaryActive: (row) => row.activestatus === 1,
  },
};

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CompactStat = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 96px;
  padding: 16px 18px;
  border-radius: ${({ theme }) => theme.rLg};
  border: ${({ theme }) => theme.cardBorder};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${({ theme }) => theme.cardShadow};
`;

const CompactIco = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  flex: none;

  svg {
    width: 19px;
    height: 19px;
  }

  ${({ $tone = 'accent', theme }) => {
    const isDark = theme.mode === 'dark';
    if ($tone === 'success') {
      return css`
        background: color-mix(in oklch, ${theme.success} ${isDark ? 22 : 14}%, ${theme.surface});
        color: ${isDark ? theme.success : '#047857'};
        box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.success} ${isDark ? 35 : 22}%, transparent);
      `;
    }
    if ($tone === 'warning') {
      return css`
        background: color-mix(in oklch, ${theme.warning} ${isDark ? 22 : 14}%, ${theme.surface});
        color: ${isDark ? 'oklch(0.78 0.13 60)' : 'oklch(0.58 0.13 60)'};
        box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.warning} ${isDark ? 35 : 22}%, transparent);
      `;
    }
    if ($tone === 'danger') {
      return css`
        background: color-mix(in oklch, ${theme.danger} ${isDark ? 20 : 12}%, ${theme.surface});
        color: ${isDark ? 'oklch(0.72 0.18 25)' : 'oklch(0.59 0.22 25)'};
        box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.danger} ${isDark ? 35 : 22}%, transparent);
      `;
    }
    return css`
      background: ${theme.accentSoft};
      color: ${theme.accentStrong};
      box-shadow: inset 0 0 0 1px color-mix(in oklch, ${theme.accent} 22%, transparent);
    `;
  }}
`;

const CompactBody = styled.div`
  flex: 1;
  min-width: 0;

  .label {
    font-size: ${({ theme }) => theme.fsMicro};
    font-weight: 650;
    color: ${({ theme }) => theme.textMuted};
    line-height: 1.2;
  }

  .value {
    margin-top: 4px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 24px;
    font-weight: 760;
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: ${({ theme }) => theme.text};

    small {
      font-size: 14px;
      color: ${({ theme }) => theme.textSubtle};
      font-weight: 600;
    }
  }

  .meta {
    margin-top: 4px;
    font-size: 12px;
    color: ${({ theme }) => theme.textSubtle};
    line-height: 1.25;
  }

  .meta--danger {
    color: ${({ theme }) => theme.danger};
  }
`;

const CompactBar = styled.div`
  height: 5px;
  border-radius: 3px;
  margin-top: 8px;
  background: ${({ theme }) => theme.surface3};
  overflow: hidden;
  box-shadow: inset 0 0 0 1px rgba(17, 20, 38, 0.04);

  > i {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: ${({ $danger, theme }) =>
      $danger
        ? 'linear-gradient(90deg, #e85d6c, #d6455d)'
        : `linear-gradient(90deg, ${theme.accent}, ${theme.accentStrong})`};
  }
`;

function StatCardSkeleton() {
  return (
    <CompactStat aria-hidden="true">
      <Skeleton $w="40px" $h="40px" $r="11px" />
      <div style={{ flex: 1 }}>
        <Skeleton $w="72px" $h="10px" $mb="8px" />
        <Skeleton $w="48px" $h="24px" $mb="8px" />
        <Skeleton $w="96px" $h="10px" />
      </div>
    </CompactStat>
  );
}

/**
 * Three-card KPI row for Courses, Assessments, and Enrollments list screens.
 * Table-derived cards render as soon as list data is ready; plan usage waits on entitlements.
 */
export default function InstructorListStatsRow({
  variant,
  entitlements,
  entitlementsLoading = false,
  rowCount,
  rows,
  loading,
  totalPages,
}) {
  const config = LIST_STATS_VARIANTS[variant];
  const meter = readMeter(entitlements, config.meterKey);
  const unlimited = meter.limit == null;
  const atCap = !unlimited && meter.limit > 0 && meter.used >= meter.limit;
  const usagePct = unlimited || !meter.limit
    ? 0
    : Math.min((meter.used / meter.limit) * 100, 100);

  const secondaryCount = useMemo(() => {
    if (!Array.isArray(rows)) return 0;
    return rows.filter(config.isSecondaryActive).length;
  }, [rows, config]);

  const secondaryMeta = totalPages > 1
    ? config.secondaryMetaPaged
    : config.secondaryMetaSingle;

  const planMeta = unlimited
    ? config.planMetaUnlimited
    : atCap
      ? 'Limit reached'
      : meter.remaining != null
        ? `${meter.remaining} slot${meter.remaining === 1 ? '' : 's'} remaining`
        : 'of plan limit';

  if (loading) {
    return (
      <StatsRow>
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </StatsRow>
    );
  }

  // Workspace total reflects THIS user's own list (rowCount = totalElements from the
  // paged list API). Never fall back to the org-wide plan meter: an invited instructor
  // with 0 of their own courses/learners must read 0 here, not the shared-org usage
  // (which the "Plan usage" card on the right already shows).
  const displayTotal = rowCount || 0;
  const planIconTone = atCap ? 'danger' : (!unlimited && usagePct >= 80 ? 'warning' : 'accent');
  const WorkspaceIcon = config.workspaceIcon;
  const planMeterLoading = entitlements === null && entitlementsLoading;

  return (
    <StatsRow>
      <CompactStat>
        <CompactIco $tone="accent">
          <WorkspaceIcon {...ICON} aria-hidden="true" />
        </CompactIco>
        <CompactBody>
          <div className="label">{config.workspaceLabel}</div>
          <div className="value tnum">{displayTotal}</div>
          <div className="meta">{config.workspaceMeta}</div>
        </CompactBody>
      </CompactStat>

      <CompactStat>
        <CompactIco $tone="success">
          <CircleCheckBig {...ICON} aria-hidden="true" />
        </CompactIco>
        <CompactBody>
          <div className="label">{config.secondaryLabel}</div>
          <div className="value tnum">{secondaryCount}</div>
          <div className="meta">{secondaryMeta}</div>
        </CompactBody>
      </CompactStat>

      {planMeterLoading ? (
        <StatCardSkeleton />
      ) : (
      <CompactStat>
        <CompactIco $tone={planIconTone}>
          <Gauge {...ICON} aria-hidden="true" />
        </CompactIco>
        <CompactBody>
          <div className="label">
            <LabelWithHelp help={config.planHelp}>Plan usage</LabelWithHelp>
          </div>
          <div className="value tnum">
            {meter.used}
            {unlimited ? (
              <small> / ∞</small>
            ) : (
              <small> / {meter.limit}</small>
            )}
          </div>
          {!unlimited && (
            <CompactBar $danger={atCap}>
              <i style={{ width: `${usagePct}%` }} />
            </CompactBar>
          )}
          <div className={`meta${atCap ? ' meta--danger' : ''}`}>{planMeta}</div>
        </CompactBody>
      </CompactStat>
      )}
    </StatsRow>
  );
}
